import React, { useState, useEffect } from "react";
import {
  AlertTriangle,
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  Loader2,
  RefreshCw,
  Shield,
  Bell,
  CheckCircle,
  Clock,
  AlertCircle,
  Users,
  FileText,
  Calendar,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Eye,
  Send,
  UserX,
  Database,
  Lock,
  AlertOctagon,
  CheckSquare,
  XCircle,
} from "lucide-react";
import { supabase } from "../lib/supabase";

// ============================================
// CONSTANTES LOI 25 - INCIDENTS (Art. 3.5)
// ============================================

const TYPES_INCIDENTS = [
  {
    id: "acces_non_autorise",
    label: "Accès non autorisé",
    desc: "Accès par une personne non habilitée",
  },
  { id: "vol", label: "Vol de données", desc: "Vol de supports ou données" },
  {
    id: "perte",
    label: "Perte de données",
    desc: "Perte accidentelle de supports ou données",
  },
  {
    id: "divulgation",
    label: "Divulgation non autorisée",
    desc: "Communication à des tiers non autorisés",
  },
  {
    id: "piratage",
    label: "Cyberattaque / Piratage",
    desc: "Attaque informatique, ransomware, etc.",
  },
  {
    id: "erreur_envoi",
    label: "Erreur d'envoi",
    desc: "Envoi à un mauvais destinataire",
  },
  {
    id: "modification",
    label: "Modification non autorisée",
    desc: "Altération des données",
  },
  {
    id: "destruction",
    label: "Destruction non autorisée",
    desc: "Suppression inappropriée",
  },
  { id: "autre", label: "Autre", desc: "Autre type d'incident" },
];

const CATEGORIES_RP = [
  "Nom et coordonnées",
  "Date de naissance",
  "Numéro d'assurance sociale",
  "Informations financières",
  "Informations bancaires",
  "Données de santé",
  "Données biométriques",
  "Informations d'emploi",
  "Photos / Vidéos",
  "Identifiants de connexion",
  "Historique de navigation",
  "Géolocalisation",
];

const CIRCONSTANCES = [
  { id: "erreur_humaine", label: "Erreur humaine" },
  { id: "defaillance_technique", label: "Défaillance technique" },
  { id: "cyberattaque", label: "Cyberattaque" },
  { id: "vol_physique", label: "Vol physique" },
  { id: "perte_appareil", label: "Perte d'appareil" },
  { id: "acces_malveillant_interne", label: "Accès malveillant (interne)" },
  { id: "acces_malveillant_externe", label: "Accès malveillant (externe)" },
  { id: "faille_fournisseur", label: "Faille chez un fournisseur" },
  { id: "autre", label: "Autre" },
];

const CRITERES_PREJUDICE_SERIEUX = [
  { id: "donnees_sensibles", label: "Données sensibles impliquées", poids: 3 },
  {
    id: "volume_important",
    label: "Grand nombre de personnes (>100)",
    poids: 2,
  },
  { id: "mineurs", label: "Mineurs concernés", poids: 3 },
  { id: "risque_financier", label: "Risque de fraude financière", poids: 3 },
  { id: "risque_identite", label: "Risque de vol d'identité", poids: 3 },
  { id: "risque_reputation", label: "Risque pour la réputation", poids: 1 },
  { id: "donnees_publiques", label: "Données rendues publiques", poids: 2 },
  { id: "acces_prolonge", label: "Accès non autorisé prolongé", poids: 2 },
];

// ============================================
// FONCTIONS SUPABASE
// ============================================

const loadIncidentsFromDB = async (entrepriseId) => {
  const { data, error } = await supabase
    .from("incidents")
    .select("*")
    .eq("entreprise_id", entrepriseId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
};

const addIncidentToDB = async (incidentData) => {
  const { data, error } = await supabase
    .from("incidents")
    .insert([incidentData])
    .select()
    .single();

  if (error) throw error;
  return data;
};

const updateIncidentInDB = async (id, updates) => {
  const { data, error } = await supabase
    .from("incidents")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

const deleteIncidentFromDB = async (id) => {
  const { error } = await supabase.from("incidents").delete().eq("id", id);
  if (error) throw error;
  return true;
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const IncidentsManager = ({ showToast, entrepriseId }) => {
  const [incidents, setIncidents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterSeverite, setFilterSeverite] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [showDetail, setShowDetail] = useState(null);

  // ============================================
  // FORMULAIRE COMPLET LOI 25 (Art. 3.5)
  // ============================================
  const initialFormData = {
    // SECTION 1: IDENTIFICATION
    type: "acces_non_autorise",
    titre: "",
    description: "",
    date_incident: new Date().toISOString().split("T")[0],
    date_decouverte: new Date().toISOString().split("T")[0],

    // SECTION 2: DONNÉES CONCERNÉES
    categories_rp: [],
    donnees_sensibles: false,
    description_donnees: "",

    // SECTION 3: PERSONNES AFFECTÉES
    nombre_personnes_estime: "",
    categories_personnes: [],
    mineurs_concernes: false,

    // SECTION 4: CIRCONSTANCES
    circonstance: "",
    description_circonstances: "",
    systemes_affectes: "",
    duree_exposition: "",

    // SECTION 5: ÉVALUATION DU PRÉJUDICE (Art. 3.5)
    criteres_prejudice: [],
    prejudice_serieux: false,
    justification_prejudice: "",
    severite: "Moyenne",

    // SECTION 6: NOTIFICATION CAI
    cai_requise: false,
    cai_notifiee: false,
    date_notification_cai: "",
    numero_dossier_cai: "",

    // SECTION 7: NOTIFICATION PERSONNES
    notification_personnes_requise: false,
    personnes_notifiees: false,
    date_notification_personnes: "",
    mode_notification: "",
    contenu_notification: "",

    // SECTION 8: MESURES ET ACTIONS
    mesures_immediates: "",
    mesures_correctives: "",
    actions_preventives: "",
    responsable_suivi: "",

    // SECTION 9: CLÔTURE
    statut: "Nouveau",
    date_cloture: "",
    lecons_apprises: "",

    // MÉTADONNÉES
    signale_par: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const sections = [
    { id: 0, title: "Identification", icon: AlertTriangle },
    { id: 1, title: "Données", icon: Database },
    { id: 2, title: "Personnes", icon: Users },
    { id: 3, title: "Circonstances", icon: FileText },
    { id: 4, title: "Préjudice", icon: AlertOctagon },
    { id: 5, title: "CAI", icon: Send },
    { id: 6, title: "Notification", icon: Bell },
    { id: 7, title: "Mesures", icon: Shield },
    { id: 8, title: "Clôture", icon: CheckSquare },
  ];

  const statuts = [
    "Nouveau",
    "En analyse",
    "En cours",
    "En attente",
    "Résolu",
    "Fermé",
  ];
  const severites = ["Faible", "Moyenne", "Élevée", "Critique"];
  const categoriesPersonnes = [
    "Clients",
    "Employés",
    "Fournisseurs",
    "Partenaires",
    "Mineurs",
    "Patients",
  ];

  const severiteColors = {
    Faible:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    Moyenne:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    Élevée:
      "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
    Critique: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  };

  const statutColors = {
    Nouveau: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    "En analyse":
      "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
    "En cours":
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    "En attente":
      "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
    Résolu:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    Fermé: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  };

  // ============================================
  // CHARGEMENT
  // ============================================
  useEffect(() => {
    if (entrepriseId) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [entrepriseId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await loadIncidentsFromDB(entrepriseId);
      setIncidents(data);
    } catch (err) {
      setError("Erreur lors du chargement des incidents.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // ÉVALUATION AUTOMATIQUE DU PRÉJUDICE
  // ============================================
  const evaluatePrejudice = () => {
    let score = 0;

    formData.criteres_prejudice.forEach((critereId) => {
      const critere = CRITERES_PREJUDICE_SERIEUX.find(
        (c) => c.id === critereId
      );
      if (critere) score += critere.poids;
    });

    // Facteurs aggravants
    if (formData.donnees_sensibles) score += 2;
    if (formData.mineurs_concernes) score += 3;
    if (parseInt(formData.nombre_personnes_estime) > 100) score += 2;
    if (parseInt(formData.nombre_personnes_estime) > 1000) score += 2;

    // Déterminer si préjudice sérieux
    const estSerieux = score >= 5;

    // Déterminer la sévérité
    let severite = "Faible";
    if (score >= 3) severite = "Moyenne";
    if (score >= 6) severite = "Élevée";
    if (score >= 10) severite = "Critique";

    return { score, estSerieux, severite };
  };

  const prejudiceEval = evaluatePrejudice();

  // Auto-update des champs basés sur l'évaluation
  useEffect(() => {
    if (showModal) {
      setFormData((prev) => ({
        ...prev,
        prejudice_serieux: prejudiceEval.estSerieux,
        cai_requise: prejudiceEval.estSerieux,
        notification_personnes_requise: prejudiceEval.estSerieux,
        severite: prejudiceEval.severite,
      }));
    }
  }, [
    formData.criteres_prejudice,
    formData.donnees_sensibles,
    formData.mineurs_concernes,
    formData.nombre_personnes_estime,
  ]);

  // ============================================
  // FILTRAGE
  // ============================================
  const filteredIncidents = incidents.filter((inc) => {
    const matchSearch =
      (inc.titre || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inc.description || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatut = filterStatut === "all" || inc.statut === filterStatut;
    const matchSeverite =
      filterSeverite === "all" || inc.severite === filterSeverite;
    return matchSearch && matchStatut && matchSeverite;
  });

  // Stats
  const stats = {
    total: incidents.length,
    nouveaux: incidents.filter((i) => i.statut === "Nouveau").length,
    enCours: incidents.filter((i) =>
      ["En analyse", "En cours", "En attente"].includes(i.statut)
    ).length,
    resolus: incidents.filter((i) => ["Résolu", "Fermé"].includes(i.statut))
      .length,
    prejudiceSerieux: incidents.filter((i) => i.prejudice_serieux).length,
    caiNotifiee: incidents.filter((i) => i.cai_notifiee).length,
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleAdd = () => {
    setEditingIncident(null);
    setFormData(initialFormData);
    setActiveSection(0);
    setShowModal(true);
  };

  const handleEdit = (incident) => {
    setEditingIncident(incident);
    setFormData({
      ...initialFormData,
      ...incident,
      categories_rp: incident.categories_rp || [],
      categories_personnes: incident.categories_personnes || [],
      criteres_prejudice: incident.criteres_prejudice || [],
    });
    setActiveSection(0);
    setShowModal(true);
  };

  const toggleArrayItem = (field, item) => {
    const current = formData[field] || [];
    if (current.includes(item)) {
      setFormData({ ...formData, [field]: current.filter((i) => i !== item) });
    } else {
      setFormData({ ...formData, [field]: [...current, item] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.description.trim()) {
      showToast?.("La description est obligatoire.", "error");
      return;
    }

    if (!entrepriseId) {
      showToast?.("Erreur: Aucune entreprise sélectionnée.", "error");
      return;
    }

    setIsSaving(true);
    try {
      const toNull = (val) =>
        val && typeof val === "string" && val.trim() !== "" ? val.trim() : null;

      const dataToSave = {
        type: formData.type,
        titre:
          toNull(formData.titre) || `Incident du ${formData.date_incident}`,
        description: formData.description.trim(),
        date_incident: toNull(formData.date_incident),
        date_decouverte: toNull(formData.date_decouverte),
        categories_rp: formData.categories_rp || [],
        donnees_sensibles: formData.donnees_sensibles || false,
        description_donnees: toNull(formData.description_donnees),
        nombre_personnes_estime: toNull(formData.nombre_personnes_estime),
        categories_personnes: formData.categories_personnes || [],
        mineurs_concernes: formData.mineurs_concernes || false,
        circonstance: toNull(formData.circonstance),
        description_circonstances: toNull(formData.description_circonstances),
        systemes_affectes: toNull(formData.systemes_affectes),
        duree_exposition: toNull(formData.duree_exposition),
        criteres_prejudice: formData.criteres_prejudice || [],
        prejudice_serieux: formData.prejudice_serieux || false,
        justification_prejudice: toNull(formData.justification_prejudice),
        severite: formData.severite || "Moyenne",
        cai_requise: formData.cai_requise || false,
        cai_notifiee: formData.cai_notifiee || false,
        date_notification_cai: toNull(formData.date_notification_cai),
        numero_dossier_cai: toNull(formData.numero_dossier_cai),
        notification_personnes_requise:
          formData.notification_personnes_requise || false,
        personnes_notifiees: formData.personnes_notifiees || false,
        date_notification_personnes: toNull(
          formData.date_notification_personnes
        ),
        mode_notification: toNull(formData.mode_notification),
        contenu_notification: toNull(formData.contenu_notification),
        mesures_immediates: toNull(formData.mesures_immediates),
        mesures_correctives: toNull(formData.mesures_correctives),
        actions_preventives: toNull(formData.actions_preventives),
        responsable_suivi: toNull(formData.responsable_suivi),
        statut: formData.statut || "Nouveau",
        date_cloture: toNull(formData.date_cloture),
        lecons_apprises: toNull(formData.lecons_apprises),
        signale_par: toNull(formData.signale_par),
        entreprise_id: entrepriseId,
      };

      if (editingIncident) {
        const updated = await updateIncidentInDB(
          editingIncident.id,
          dataToSave
        );
        setIncidents(
          incidents.map((i) => (i.id === editingIncident.id ? updated : i))
        );
        showToast?.("Incident modifié avec succès!", "success");
      } else {
        const newIncident = await addIncidentToDB(dataToSave);
        setIncidents([newIncident, ...incidents]);
        showToast?.("Incident signalé avec succès!", "success");
      }
      setShowModal(false);
    } catch (err) {
      console.error("Erreur détaillée:", err);
      showToast?.(
        `Erreur: ${err.message || "Erreur lors de l'enregistrement."}`,
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteIncidentFromDB(id);
      setIncidents(incidents.filter((i) => i.id !== id));
      showToast?.("Incident supprimé.", "success");
    } catch (err) {
      showToast?.("Erreur lors de la suppression.", "error");
    }
    setShowDeleteConfirm(null);
  };

  // ============================================
  // RENDU
  // ============================================
  if (!entrepriseId) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto text-amber-500 mb-4" size={48} />
        <p className="text-gray-600 dark:text-gray-400">
          Veuillez sélectionner une entreprise.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={40} className="text-red-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <AlertTriangle className="text-red-600" />
            Gestion des incidents
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Article 3.5 de la Loi 25 — Registre des incidents de confidentialité
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition font-medium shadow-lg"
        >
          <Plus size={20} />
          Signaler un incident
        </button>
      </div>

      {/* Alerte CAI */}
      {stats.prejudiceSerieux > 0 &&
        stats.caiNotifiee < stats.prejudiceSerieux && (
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertOctagon
                className="text-red-600 flex-shrink-0 mt-0.5"
                size={24}
              />
              <div>
                <h3 className="font-semibold text-red-800 dark:text-red-300">
                  Notification CAI requise
                </h3>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                  {stats.prejudiceSerieux - stats.caiNotifiee} incident(s) avec
                  préjudice sérieux nécessite(nt) une notification à la
                  Commission d'accès à l'information dans les plus brefs délais.
                </p>
                <a
                  href="https://www.cai.gouv.qc.ca/incident-de-confidentialite/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  Accéder au formulaire CAI
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </div>
        )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats.total}
          </p>
          <p className="text-xs text-gray-500">Total</p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
            {stats.nouveaux}
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400">Nouveaux</p>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
          <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">
            {stats.enCours}
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-400">
            En traitement
          </p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <p className="text-2xl font-bold text-green-700 dark:text-green-400">
            {stats.resolus}
          </p>
          <p className="text-xs text-green-600 dark:text-green-400">Résolus</p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
          <p className="text-2xl font-bold text-red-700 dark:text-red-400">
            {stats.prejudiceSerieux}
          </p>
          <p className="text-xs text-red-600 dark:text-red-400">
            Préjudice sérieux
          </p>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
          <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">
            {stats.caiNotifiee}
          </p>
          <p className="text-xs text-purple-600 dark:text-purple-400">
            CAI notifiée
          </p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl"
          />
        </div>
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl"
        >
          <option value="all">Tous les statuts</option>
          {statuts.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={filterSeverite}
          onChange={(e) => setFilterSeverite(e.target.value)}
          className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl"
        >
          <option value="all">Toutes sévérités</option>
          {severites.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          onClick={loadData}
          className="p-2.5 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <RefreshCw size={20} className="text-gray-600 dark:text-gray-300" />
        </button>
      </div>

      {/* Liste */}
      {error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <AlertTriangle className="text-red-500 mx-auto mb-2" size={32} />
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      ) : filteredIncidents.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-12 text-center">
          <Shield className="text-gray-400 mx-auto mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
            Aucun incident signalé
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            C'est une bonne nouvelle! Votre registre d'incidents est vide.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredIncidents.map((incident) => (
            <div
              key={incident.id}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-md border overflow-hidden ${
                incident.prejudice_serieux && !incident.cai_notifiee
                  ? "border-red-300 dark:border-red-700"
                  : "border-gray-200 dark:border-gray-700"
              }`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {incident.titre ||
                          `Incident #${incident.id?.slice(0, 8)}`}
                      </h3>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          severiteColors[incident.severite] ||
                          severiteColors.Moyenne
                        }`}
                      >
                        {incident.severite || "Moyenne"}
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          statutColors[incident.statut] || statutColors.Nouveau
                        }`}
                      >
                        {incident.statut || "Nouveau"}
                      </span>
                      {incident.prejudice_serieux && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300 flex items-center gap-1">
                          <AlertOctagon size={12} />
                          Préjudice sérieux
                        </span>
                      )}
                      {incident.cai_notifiee && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300">
                          CAI notifiée
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                      {incident.description}
                    </p>

                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {incident.date_incident
                          ? new Date(incident.date_incident).toLocaleDateString(
                              "fr-CA"
                            )
                          : "Date inconnue"}
                      </span>
                      {incident.nombre_personnes_estime && (
                        <span className="flex items-center gap-1">
                          <Users size={14} />~{incident.nombre_personnes_estime}{" "}
                          personnes
                        </span>
                      )}
                      {incident.categories_rp?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Database size={14} />
                          {incident.categories_rp.length} type(s) de RP
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        setShowDetail(
                          showDetail === incident.id ? null : incident.id
                        )
                      }
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => handleEdit(incident)}
                      className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/30 rounded-lg"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(incident.id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Détails dépliables */}
                {showDetail === incident.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid md:grid-cols-2 gap-4 text-sm">
                    {incident.circonstance && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Circonstance:
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {CIRCONSTANCES.find(
                            (c) => c.id === incident.circonstance
                          )?.label || incident.circonstance}
                        </p>
                      </div>
                    )}
                    {incident.mesures_immediates && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Mesures immédiates:
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {incident.mesures_immediates}
                        </p>
                      </div>
                    )}
                    {incident.mesures_correctives && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Mesures correctives:
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {incident.mesures_correctives}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Confirmation suppression */}
              {showDeleteConfirm === incident.id && (
                <div className="bg-red-50 dark:bg-red-900/20 p-4 border-t border-red-200 dark:border-red-800">
                  <p className="text-red-700 dark:text-red-300 mb-3">
                    Supprimer cet incident? Cette action est irréversible.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDelete(incident.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Supprimer
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(null)}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL FORMULAIRE */}
      {/* ============================================ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b dark:border-gray-700 flex items-center justify-between bg-red-50 dark:bg-red-900/20">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editingIncident
                    ? "Modifier l'incident"
                    : "Signaler un incident"}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Registre des incidents de confidentialité (Art. 3.5)
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            {/* Alerte préjudice */}
            {prejudiceEval.estSerieux && (
              <div className="mx-6 mt-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg p-3">
                <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
                  <AlertOctagon size={20} />
                  <span className="font-semibold">
                    Préjudice sérieux détecté (score: {prejudiceEval.score})
                  </span>
                </div>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                  Vous devez notifier la CAI et les personnes concernées dans
                  les plus brefs délais.
                </p>
              </div>
            )}

            {/* Navigation sections */}
            <div className="px-4 py-3 border-b dark:border-gray-700 overflow-x-auto">
              <div className="flex gap-1 min-w-max">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                      activeSection === section.id
                        ? "bg-red-600 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200"
                    }`}
                  >
                    <section.icon size={16} />
                    <span className="hidden md:inline">{section.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contenu */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* SECTION 0: IDENTIFICATION */}
                {activeSection === 0 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <AlertTriangle size={20} className="text-red-600" />
                      Identification de l'incident
                    </h4>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Type d'incident *
                        </label>
                        <select
                          value={formData.type}
                          onChange={(e) =>
                            setFormData({ ...formData, type: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        >
                          {TYPES_INCIDENTS.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Titre
                        </label>
                        <input
                          type="text"
                          value={formData.titre}
                          onChange={(e) =>
                            setFormData({ ...formData, titre: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: Fuite de données clients"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description de l'incident *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Décrivez l'incident en détail..."
                        required
                      />
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Date de l'incident *
                        </label>
                        <input
                          type="date"
                          value={formData.date_incident}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_incident: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Date de découverte
                        </label>
                        <input
                          type="date"
                          value={formData.date_decouverte}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_decouverte: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Signalé par
                        </label>
                        <input
                          type="text"
                          value={formData.signale_par}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              signale_par: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Nom de la personne"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* SECTION 1: DONNÉES CONCERNÉES */}
                {activeSection === 1 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Database size={20} className="text-red-600" />
                      Renseignements personnels concernés
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Catégories de RP impliquées
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {CATEGORIES_RP.map((cat) => (
                          <label
                            key={cat}
                            className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <input
                              type="checkbox"
                              checked={formData.categories_rp?.includes(cat)}
                              onChange={() =>
                                toggleArrayItem("categories_rp", cat)
                              }
                              className="rounded border-gray-300 text-red-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {cat}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <label className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.donnees_sensibles}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            donnees_sensibles: e.target.checked,
                          })
                        }
                        className="w-5 h-5 rounded border-gray-300 text-amber-600"
                      />
                      <div>
                        <span className="font-medium text-amber-800 dark:text-amber-300">
                          Données sensibles impliquées
                        </span>
                        <p className="text-xs text-amber-700 dark:text-amber-400">
                          Santé, biométriques, mineurs, opinions politiques,
                          etc.
                        </p>
                      </div>
                    </label>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description des données concernées
                      </label>
                      <textarea
                        value={formData.description_donnees}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description_donnees: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Décrivez précisément les données concernées..."
                      />
                    </div>
                  </div>
                )}

                {/* SECTION 2: PERSONNES AFFECTÉES */}
                {activeSection === 2 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Users size={20} className="text-red-600" />
                      Personnes affectées
                    </h4>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Nombre estimé de personnes
                        </label>
                        <input
                          type="number"
                          value={formData.nombre_personnes_estime}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              nombre_personnes_estime: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: 150"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Catégories de personnes
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {categoriesPersonnes.map((cat) => (
                          <label
                            key={cat}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <input
                              type="checkbox"
                              checked={formData.categories_personnes?.includes(
                                cat
                              )}
                              onChange={() =>
                                toggleArrayItem("categories_personnes", cat)
                              }
                              className="rounded border-gray-300 text-red-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {cat}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <label className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg cursor-pointer border-l-4 border-red-500">
                      <input
                        type="checkbox"
                        checked={formData.mineurs_concernes}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mineurs_concernes: e.target.checked,
                          })
                        }
                        className="w-5 h-5 rounded border-gray-300 text-red-600"
                      />
                      <div>
                        <span className="font-medium text-red-800 dark:text-red-300">
                          Des mineurs sont concernés
                        </span>
                        <p className="text-xs text-red-700 dark:text-red-400">
                          Facteur aggravant pour l'évaluation du préjudice
                        </p>
                      </div>
                    </label>
                  </div>
                )}

                {/* SECTION 3: CIRCONSTANCES */}
                {activeSection === 3 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <FileText size={20} className="text-red-600" />
                      Circonstances de l'incident
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cause principale
                      </label>
                      <select
                        value={formData.circonstance}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            circonstance: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                      >
                        <option value="">-- Sélectionner --</option>
                        {CIRCONSTANCES.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description des circonstances
                      </label>
                      <textarea
                        value={formData.description_circonstances}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description_circonstances: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Comment l'incident s'est-il produit?"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Systèmes affectés
                        </label>
                        <input
                          type="text"
                          value={formData.systemes_affectes}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              systemes_affectes: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: CRM, serveur de fichiers..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Durée d'exposition estimée
                        </label>
                        <input
                          type="text"
                          value={formData.duree_exposition}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              duree_exposition: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: 2 heures, 3 jours..."
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* SECTION 4: ÉVALUATION DU PRÉJUDICE */}
                {activeSection === 4 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <AlertOctagon size={20} className="text-red-600" />
                      Évaluation du préjudice (Art. 3.5)
                    </h4>

                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Cochez les critères qui s'appliquent pour évaluer si un
                        préjudice sérieux est probable:
                      </p>
                      <div className="grid md:grid-cols-2 gap-2">
                        {CRITERES_PREJUDICE_SERIEUX.map((critere) => (
                          <label
                            key={critere.id}
                            className="flex items-start gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={formData.criteres_prejudice?.includes(
                                critere.id
                              )}
                              onChange={() =>
                                toggleArrayItem(
                                  "criteres_prejudice",
                                  critere.id
                                )
                              }
                              className="mt-1 rounded border-gray-300 text-red-600"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {critere.label}
                              </span>
                              <span className="ml-2 text-xs text-gray-500">
                                (poids: {critere.poids})
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Résultat de l'évaluation */}
                    <div
                      className={`p-4 rounded-lg border-l-4 ${
                        prejudiceEval.estSerieux
                          ? "bg-red-50 dark:bg-red-900/20 border-red-500"
                          : "bg-green-50 dark:bg-green-900/20 border-green-500"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {prejudiceEval.estSerieux ? (
                          <AlertOctagon className="text-red-600" size={24} />
                        ) : (
                          <CheckCircle className="text-green-600" size={24} />
                        )}
                        <div>
                          <p
                            className={`font-semibold ${
                              prejudiceEval.estSerieux
                                ? "text-red-800 dark:text-red-300"
                                : "text-green-800 dark:text-green-300"
                            }`}
                          >
                            Score: {prejudiceEval.score} —{" "}
                            {prejudiceEval.estSerieux
                              ? "PRÉJUDICE SÉRIEUX PROBABLE"
                              : "Préjudice sérieux peu probable"}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Sévérité calculée:{" "}
                            <strong>{prejudiceEval.severite}</strong>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Justification de l'évaluation
                      </label>
                      <textarea
                        value={formData.justification_prejudice}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            justification_prejudice: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Expliquez votre évaluation du risque de préjudice..."
                      />
                    </div>
                  </div>
                )}

                {/* SECTION 5: NOTIFICATION CAI */}
                {activeSection === 5 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Send size={20} className="text-red-600" />
                      Notification à la CAI
                    </h4>

                    {formData.cai_requise && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <AlertOctagon
                            className="text-red-600 flex-shrink-0"
                            size={20}
                          />
                          <div>
                            <p className="font-semibold text-red-800 dark:text-red-300">
                              Notification obligatoire
                            </p>
                            <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                              Un préjudice sérieux étant probable, vous devez
                              notifier la CAI avec diligence (sans délai
                              déraisonnable).
                            </p>
                            <a
                              href="https://www.cai.gouv.qc.ca/incident-de-confidentialite/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
                            >
                              Formulaire de déclaration CAI
                              <ExternalLink size={14} />
                            </a>
                          </div>
                        </div>
                      </div>
                    )}

                    <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.cai_notifiee}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            cai_notifiee: e.target.checked,
                          })
                        }
                        className="w-5 h-5 rounded border-gray-300 text-purple-600"
                      />
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        La CAI a été notifiée
                      </span>
                    </label>

                    {formData.cai_notifiee && (
                      <div className="grid md:grid-cols-2 gap-4 ml-8">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Date de notification
                          </label>
                          <input
                            type="date"
                            value={formData.date_notification_cai}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                date_notification_cai: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Numéro de dossier CAI
                          </label>
                          <input
                            type="text"
                            value={formData.numero_dossier_cai}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                numero_dossier_cai: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                            placeholder="Ex: 2026-XXXX"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* SECTION 6: NOTIFICATION PERSONNES */}
                {activeSection === 6 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Bell size={20} className="text-red-600" />
                      Notification aux personnes concernées
                    </h4>

                    {formData.notification_personnes_requise && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                        <p className="font-semibold text-amber-800 dark:text-amber-300">
                          Notification recommandée
                        </p>
                        <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                          Un préjudice sérieux étant probable, les personnes
                          concernées devraient être informées de l'incident.
                        </p>
                      </div>
                    )}

                    <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.personnes_notifiees}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            personnes_notifiees: e.target.checked,
                          })
                        }
                        className="w-5 h-5 rounded border-gray-300 text-blue-600"
                      />
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        Les personnes concernées ont été notifiées
                      </span>
                    </label>

                    {formData.personnes_notifiees && (
                      <div className="space-y-4 ml-8">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Date de notification
                            </label>
                            <input
                              type="date"
                              value={formData.date_notification_personnes}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  date_notification_personnes: e.target.value,
                                })
                              }
                              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Mode de notification
                            </label>
                            <input
                              type="text"
                              value={formData.mode_notification}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  mode_notification: e.target.value,
                                })
                              }
                              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                              placeholder="Ex: Courriel, lettre, téléphone..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Contenu de la notification
                          </label>
                          <textarea
                            value={formData.contenu_notification}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                contenu_notification: e.target.value,
                              })
                            }
                            rows={3}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                            placeholder="Résumé du message envoyé aux personnes..."
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* SECTION 7: MESURES */}
                {activeSection === 7 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Shield size={20} className="text-red-600" />
                      Mesures et actions
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Mesures immédiates prises
                      </label>
                      <textarea
                        value={formData.mesures_immediates}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mesures_immediates: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Quelles actions ont été prises immédiatement pour limiter les dommages?"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Mesures correctives
                      </label>
                      <textarea
                        value={formData.mesures_correctives}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mesures_correctives: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Quelles mesures sont mises en place pour corriger la situation?"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Actions préventives
                      </label>
                      <textarea
                        value={formData.actions_preventives}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            actions_preventives: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Quelles mesures éviteront que cet incident se reproduise?"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Responsable du suivi
                      </label>
                      <input
                        type="text"
                        value={formData.responsable_suivi}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            responsable_suivi: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Nom de la personne responsable"
                      />
                    </div>
                  </div>
                )}

                {/* SECTION 8: CLÔTURE */}
                {activeSection === 8 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <CheckSquare size={20} className="text-red-600" />
                      Clôture de l'incident
                    </h4>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Statut
                        </label>
                        <select
                          value={formData.statut}
                          onChange={(e) =>
                            setFormData({ ...formData, statut: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        >
                          {statuts.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Date de clôture
                        </label>
                        <input
                          type="date"
                          value={formData.date_cloture}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_cloture: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Leçons apprises
                      </label>
                      <textarea
                        value={formData.lecons_apprises}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            lecons_apprises: e.target.value,
                          })
                        }
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Quelles leçons tirer de cet incident pour l'avenir?"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setActiveSection(Math.max(0, activeSection - 1))
                    }
                    disabled={activeSection === 0}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                  >
                    ← Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setActiveSection(
                        Math.min(sections.length - 1, activeSection + 1)
                      )
                    }
                    disabled={activeSection === sections.length - 1}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                  >
                    Suivant →
                  </button>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-5 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        {editingIncident ? "Modifier" : "Signaler"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentsManager;
